import Table, { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import React from 'react';
import { Link } from 'react-router-dom';
import { defaultTablePagination } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import AuthenticatedAttachmentImage from '../../../components/authenticated-attachment-image';
import { getSearchFilter } from '../../../helpers/FilterHelper';
import { getTableLocale } from '../../../helpers/TableHelper';
import { ToolVm, ToolsClient } from '../../../utils/api';
import { Props } from './index';
import {
    applySearchParams,
    getFilterParamsString,
    getFiltersParams,
    getPagingSortingParams,
} from '../../../helpers/SearchParamsHelper';

interface State {
    tools?: ToolVm[];
    loading?: boolean;
    totalItems?: number;
}

class ToolTable extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            tools: [],
            totalItems: 0,
        };
    }

    public componentDidMount = () => {
        this.getTools();
    };

    public componentDidUpdate = (prevProps: Props) => {
        const { location } = this.props;

        if (prevProps.location.search !== location.search) {
            this.getTools();
        }
    };

    private handleTableChange = async (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolVm> | SorterResult<ToolVm>[] = {}
    ) => {
        const {
            history,
            location: { search },
        } = this.props;

        const currSearchParams = new URLSearchParams(search);
        const tableSearchParams = this.createTableSearchParams(pagination, filters, sorter);
        const newSearchParams = applySearchParams(currSearchParams, tableSearchParams);

        history.push({
            pathname: history.location.pathname,
            search: newSearchParams.toString(),
        });
    };

    private createTableSearchParams = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolVm> | SorterResult<ToolVm>[] = {}
    ): URLSearchParams => {
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const filterParamsString = getFilterParamsString(filters);
        const singleSorter = sorter as SorterResult<ToolVm>;
        const tableSearchParams = new URLSearchParams(filterParamsString);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';

        tableSearchParams.append('pageIndex', pagination.current?.toString() || '1');
        tableSearchParams.append(
            'pageSize',
            pagination.pageSize?.toString() || pagination.defaultPageSize?.toString() || ''
        );
        tableSearchParams.append(
            'sortBy',
            singleSorter.field?.toString() && singleSorter.order
                ? singleSorter.field?.toString()
                : ''
        );
        tableSearchParams.append('sortOrder', singleSorter.order || '');
        tableSearchParams.append('activeTab', activeTab ? activeTab : 'active');

        return tableSearchParams;
    };

    private getTools = async () => {
        this.setState({
            loading: true,
        });

        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';
        const { pageIndexParam, pageSizeParam, sortByParam, sortOrderParam } =
            getPagingSortingParams(params);
        const filters = getFiltersParams(params);

        const result = await new ToolsClient().getPaginated(
            filters.name ? filters.name.toString() : undefined,
            filters.description ? filters.description.toString() : undefined,
            activeTab === 'archived',
            pageIndexParam && parseInt(pageIndexParam) ? parseInt(pageIndexParam) : 1,
            pageSizeParam && parseInt(pageSizeParam)
                ? parseInt(pageSizeParam)
                : defaultTablePagination.defaultPageSize,
            sortByParam || undefined,
            sortOrderParam || undefined
        );

        this.setState({
            tools: result.pageItems,
            loading: false,
            totalItems: result.totalItems,
        });
    };

    public render(): React.ReactElement {
        const { tools, loading, totalItems } = this.state;
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const { sortByParam, sortOrderParam, pageIndexParam, pageSizeParam } =
            getPagingSortingParams(params);
        const filters = getFiltersParams(params);
        const pagination: TablePaginationConfig = {
            ...defaultTablePagination,
            current:
                pageIndexParam && parseInt(pageIndexParam)
                    ? parseInt(pageIndexParam)
                    : defaultTablePagination.defaultCurrent,
            pageSize:
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
            total: totalItems,
        };

        const columns = [
            {
                title: translations.tools.image,
                dataIndex: 'thumbnailAttachmentId',
                width: 88,
                render: (_: unknown, record: ToolVm): React.ReactElement | null =>
                    record.thumbnailAttachmentId ? (
                        <AuthenticatedAttachmentImage
                            attachmentId={record.thumbnailAttachmentId}
                            alt=""
                            width={56}
                            height={56}
                        />
                    ) : null,
            },
            {
                title: translations.projects.name,
                dataIndex: 'name',
                ...getSearchFilter(),
                sorter: true,
                filteredValue: filters.name ? [filters.name] : null,
                sortOrder: sortByParam === 'name' ? sortOrderParam : null,
                render: (value: string, record: ToolVm): React.ReactElement => (
                    <Link
                        to={Routes.ROUTE_TOOLS_READ.replace(
                            ReplaceStrings.ENTITY_ID,
                            record.id.toString()
                        )}
                    >
                        {value}
                    </Link>
                ),
            },
            {
                title: translations.projects.description,
                dataIndex: 'description',
                ...getSearchFilter(),
                filteredValue: filters.description ? [filters.description] : null,
                sorter: true,
                sortOrder: sortByParam === 'description' ? sortOrderParam : null,
            },
            {
                title: translations.general.actions,
                key: 'actions',
                width: 100,
                align: 'left',
                className: 'no-print',
                render: (value: ToolVm): React.ReactElement => (
                    <Link
                        to={Routes.ROUTE_TOOLS_READ.replace(
                            ReplaceStrings.ENTITY_ID,
                            value.id.toString()
                        )}
                    >
                        {translations.general.details}
                    </Link>
                ),
            },
        ];

        return (
            <Table
                locale={getTableLocale()}
                columns={columns}
                dataSource={tools}
                loading={loading}
                rowKey={(record: ToolVm): string => record.id?.toString()}
                onChange={this.handleTableChange}
                pagination={pagination}
                bordered
                size="small"
            />
        );
    }
}

export default ToolTable;

